/**
 * Where the confetti goes.
 *
 * A settled balance is the one moment this app celebrates, and a burst is the
 * one animation in it with no meaning to carry — so the whole of it is here,
 * as arithmetic, and the drawing is fourteen `<span>`s and a keyframe. No
 * canvas, no requestAnimationFrame and no physics engine: each piece is given
 * an apex, a landing and a spin, and the browser's compositor does the rest on
 * a thread this app does not have to be fast on.
 *
 * **Deterministic.** `noise` is a hash of the piece's index, not
 * `Math.random`, so the same burst comes out the same twice and a test can
 * hold it. Nobody can tell the difference between fourteen pieces that are
 * always in the same places and fourteen that are not; a test can tell the
 * difference between arithmetic that works and arithmetic that produces
 * `NaN`, and only one of those is worth having.
 *
 * No colours here either — the pieces are tinted in `src/index.css` from the
 * theme's own tokens, like everything else in this app.
 */

/** One piece: where it goes, how it turns, and when it sets off. In px, deg, ms. */
export type ConfettiPiece = {
  /** How far sideways it ends up. Negative is left. */
  x: number
  /** The top of its arc. Always negative — up, before anything falls. */
  peak: number
  /** Where it ends up, below where it started, having fallen past it. */
  y: number
  /** Degrees, either way round. */
  turn: number
  /** A few milliseconds apart, so fourteen pieces are not one shape. */
  delayMs: number
}

/** Enough to read as a burst, few enough to be fourteen spans and no more. */
export const CONFETTI_PIECES = 14

/** A hash of an integer into 0..1. Deterministic, and quite good enough. */
function noise(n: number): number {
  const x = Math.sin(n * 127.1 + 0.5) * 43758.5453
  return x - Math.floor(x)
}

/**
 * The burst, fanned upwards.
 *
 * The pieces are spread evenly across an arc a little under a half-turn wide,
 * nudged off the even spacing by their own noise so the fan does not read as a
 * comb. Every one of them goes up first (`peak` is negative) and lands below
 * where it started (`y` is greater than `peak`), which is what makes fourteen
 * independent transforms look like one thing thrown in the air.
 */
export function confettiBurst(count: number = CONFETTI_PIECES): ConfettiPiece[] {
  const pieces: ConfettiPiece[] = []

  for (let i = 0; i < count; i += 1) {
    const wobble = noise(i)
    const spread = count === 1 ? 0.5 : (i + wobble * 0.7) / count
    // Straight up is -π/2; the fan is ±55° either side of it.
    const angle = -Math.PI / 2 + (spread - 0.5) * 1.92
    const distance = 42 + noise(i + 7) * 38

    const peak = Math.sin(angle) * distance
    pieces.push({
      x: Math.round(Math.cos(angle) * distance),
      peak: Math.round(Math.min(peak, -6)),
      y: Math.round(Math.min(peak, -6) + 54 + noise(i + 13) * 46),
      turn: Math.round((noise(i + 21) - 0.5) * 720),
      delayMs: (i % 4) * 22,
    })
  }

  return pieces
}
